// @flow
import React, { useEffect } from 'react';
import type { Node } from 'react';
import { Button } from '@fortress-technology-solutions/fortress-component-library/Atoms';
import {
  PencilIcon,
  UploadIcon,
} from '@fortress-technology-solutions/fortress-component-library/Icons';

import ResidentService from '../../services/residentService';
import useIntl from '../../hooks/useIntl';
import { impactMessages as messages } from './messages';
import {
  CertificationImpactRow,
  ImpactList,
  ImpactRow,
  NO_DATE,
  PreviewStatusView,
  countImpactRecords,
  toDisplay,
  useCorrectionPreview,
} from './correctionImpacts';
import type {
  DateFieldValue,
  PreviewCert,
  PreviewWarning,
  StatusChangeHandler,
} from './correctionImpacts';

type MoveInCorrectionImpactsData = {
  currentMoveInDate: string,
  newMoveInDate: string,
  /** The corrected stay. */
  stay?: ?{
    stayLeaseId: string,
    unitLabel: string,
    isCurrent: boolean,
    moveOutDate: ?string,
    endedBy: ?('TRANSFER' | 'MOVE_OUT'),
  },
  /** Current stay only (null for a previous stay). */
  activeLease: ?{
    id: string,
    currentScheduledMoveInDate: ?string,
    newScheduledMoveInDate: string,
    correctedLeaseRecommended: boolean,
    /** The user may upload the Corrected Lease with this save (scope + document type enabled). */
    correctedLeaseUpload?: ?{ available: boolean },
  },
  /** A previous stay's leases whose Scheduled Move-In changes ([] for the current stay). */
  stayLeases?: Array<{
    id: string,
    startDate: string,
    currentScheduledMoveInDate: ?string,
    newScheduledMoveInDate: string,
  }>,
  certifications: Array<PreviewCert>,
  warnings: Array<PreviewWarning>,
  /** A stay created by a transfer: the unit it came from, whose Move-Out moves too. */
  transfer: ?{
    originUnit: { id: string, number: string },
    currentMoveOutDate: string,
    newMoveOutDate: string,
  },
};

type Props = {
  residentId: string,
  organizationId: string,
  propertyId: string,
  /** The move-in lease of the stay being corrected. */
  stayLeaseId: ?string,
  /** The date field's value; only previewed once the client rules pass. */
  proposedDate: DateFieldValue,
  /** YYYY-MM-DD; an unchanged date has nothing to preview. */
  currentMoveInDate: ?string,
  /** The form's own validation (required, format, future, fiscal) passed. */
  clientValid: boolean,
  /**
   * The status, with the YYYY-MM-DD date it answers (null when there is none) and the number of
   * records listed (0 unless valid).
   */
  onStatusChange: StatusChangeHandler,
  /** The Corrected Lease file held for the save, if any (ENG-33327). */
  correctedLease?: ?File,
  /** Opens the Upload Corrected Lease dialog. */
  onUploadCorrectedLease?: () => void,
  /** A valid preview no longer offers the Corrected Lease upload: the held file must go (UL8). */
  onCorrectedLeaseNotOffered?: () => void,
  /** The Active Lease row's Upload / Edit uploaded button, focused on return from the dialog. */
  uploadButtonRef?: { current: ?HTMLElement },
  /** The modal hides for the upload dialog: keep the previews for its return. */
  keepPreviewCache?: boolean,
};
/** The record-count header; it names the modal's scrolling impact region (aria-labelledby). */
export const IMPACTS_COUNT_ID = 'mi-impacts-count-label';

/**
 * The Corrected Lease can be uploaded with the save: the backend recommends it for this date and
 * the user can upload it at this property (UL1, UL9).
 */
const isCorrectedLeaseUploadOffered = (
  impacts: ?MoveInCorrectionImpactsData,
): boolean =>
  Boolean(
    impacts?.activeLease?.correctedLeaseRecommended &&
      impacts.activeLease.correctedLeaseUpload?.available,
  );

/** The Active Lease row: the Scheduled Move-In change and, when offered, the upload action. */
const ActiveLeaseImpactRow = ({
  activeLease,
  uploadOffered,
  correctedLease,
  onUploadCorrectedLease,
  uploadButtonRef,
}: {
  activeLease: $NonMaybeType<
    $PropertyType<MoveInCorrectionImpactsData, 'activeLease'>,
  >,
  uploadOffered: boolean,
  correctedLease: ?File,
  onUploadCorrectedLease?: () => void,
  uploadButtonRef?: { current: ?HTMLElement },
}): Node => {
  const intl = useIntl();
  const date = toDisplay(activeLease.newScheduledMoveInDate);
  const title = intl.formatMessage(messages.activeLeaseTitle);

  if (uploadOffered) {
    const held = Boolean(correctedLease);
    return (
      <ImpactRow
        dataTest="mi-impact-active-lease"
        title={title}
        description={intl.formatMessage(
          held
            ? messages.activeLeaseUploadedDescription
            : messages.activeLeaseUploadDescription,
          { date },
        )}
        chip={intl.formatMessage(
          held ? messages.uploaded : messages.actionNeeded,
        )}
        chipColor={held ? 'success' : 'warning'}
        action={
          <Button
            variant="primarySubtle"
            size="small"
            fullWidth
            ref={uploadButtonRef}
            startIcon={held ? <PencilIcon /> : <UploadIcon />}
            onClick={onUploadCorrectedLease}
            data-test="mi-impact-corrected-lease-upload"
          >
            {intl.formatMessage(
              held
                ? messages.editUploadedCorrectedLease
                : messages.uploadCorrectedLease,
            )}
          </Button>
        }
      />
    );
  }
  return (
    <ImpactRow
      dataTest="mi-impact-active-lease"
      title={title}
      description={[
        intl.formatMessage(messages.activeLeaseDescription, { date }),
        activeLease.correctedLeaseRecommended
          ? intl.formatMessage(messages.correctedLeaseRecommended)
          : '',
      ]
        .filter(Boolean)
        .join(' ')}
      chip={intl.formatMessage(
        activeLease.correctedLeaseRecommended
          ? messages.actionNeeded
          : messages.autoUpdated,
      )}
      chipColor={activeLease.correctedLeaseRecommended ? 'warning' : 'primary'}
    />
  );
};

/** The affected-record list of a valid Move-In Date correction preview. */
const MoveInImpactList = ({
  impacts,
  correctedLease,
  onUploadCorrectedLease,
  uploadButtonRef,
}: {
  impacts: ?MoveInCorrectionImpactsData,
  correctedLease: ?File,
  onUploadCorrectedLease?: () => void,
  uploadButtonRef?: { current: ?HTMLElement },
}): Node => {
  const intl = useIntl();
  const recordCount = countImpactRecords(impacts);
  if (!impacts || !recordCount) {
    return null;
  }
  const { activeLease, stayLeases, certifications, warnings, transfer } =
    impacts;

  return (
    <ImpactList
      testIdPrefix="mi"
      countId={IMPACTS_COUNT_ID}
      reviewBanner={intl.formatMessage(messages.reviewBanner)}
      recordCount={recordCount}
      warnings={warnings}
    >
      {transfer && (
        <ImpactRow
          dataTest="mi-impact-origin-move-out"
          title={intl.formatMessage(messages.originMoveOutTitle, {
            unitNumber: transfer.originUnit.number,
          })}
          description={intl.formatMessage(messages.originMoveOutDescription, {
            currentDate: toDisplay(transfer.currentMoveOutDate),
            date: toDisplay(transfer.newMoveOutDate),
          })}
          chip={intl.formatMessage(messages.autoUpdated)}
          chipColor="primary"
        />
      )}
      {certifications?.map((cert) => (
        <CertificationImpactRow
          key={cert.id}
          cert={cert}
          dataTest="mi-impact-cert"
          needsReviewDescription={intl.formatMessage(
            messages.needsReviewDescription,
            { formName: cert.formName },
          )}
        />
      ))}
      {activeLease && (
        <ActiveLeaseImpactRow
          activeLease={activeLease}
          uploadOffered={isCorrectedLeaseUploadOffered(impacts)}
          correctedLease={correctedLease}
          onUploadCorrectedLease={onUploadCorrectedLease}
          uploadButtonRef={uploadButtonRef}
        />
      )}
      {/* A previous stay's leases: their Scheduled Move-In moves, never a Corrected Lease (PS3). */}
      {stayLeases?.map((stayLease) => (
        <ImpactRow
          key={stayLease.id}
          dataTest="mi-impact-stay-lease"
          title={intl.formatMessage(messages.stayLeaseTitle, {
            date: toDisplay(stayLease.startDate),
          })}
          description={intl.formatMessage(messages.stayLeaseDescription, {
            currentDate: stayLease.currentScheduledMoveInDate
              ? toDisplay(stayLease.currentScheduledMoveInDate)
              : NO_DATE,
            date: toDisplay(stayLease.newScheduledMoveInDate),
          })}
          chip={intl.formatMessage(messages.autoUpdated)}
          chipColor="primary"
        />
      ))}
    </ImpactList>
  );
};

/**
 * The affected-record list of the Edit Move-In Date modal (household-mi-mo-date-changes). Once the
 * entered date passes the form's rules it asks the backend for the dry-run plan of that date for
 * the stay being corrected and lists only the impacts that apply. Reports its status so the modal
 * can gate Save on it.
 */
const MoveInCorrectionImpacts = ({
  residentId,
  organizationId,
  propertyId,
  stayLeaseId,
  proposedDate,
  currentMoveInDate,
  clientValid,
  onStatusChange,
  correctedLease,
  onUploadCorrectedLease,
  onCorrectedLeaseNotOffered,
  uploadButtonRef,
  keepPreviewCache,
}: Props): Node => {
  const { status, impacts, message, retry } =
    useCorrectionPreview<MoveInCorrectionImpactsData>({
      queryKey: [
        'moveInDateCorrectionPreview',
        organizationId,
        propertyId,
        residentId,
        stayLeaseId,
      ],
      proposedDate,
      currentDate: currentMoveInDate,
      clientValid,
      fetchPreview: (date, signal) =>
        new ResidentService().previewMoveInDateCorrection(
          residentId,
          organizationId,
          propertyId,
          date,
          stayLeaseId,
          signal,
        ),
      onStatusChange,
      keepCacheOnUnmount: keepPreviewCache,
    });

  const uploadOffered = isCorrectedLeaseUploadOffered(impacts);
  useEffect(() => {
    if (status === 'valid' && !uploadOffered) {
      onCorrectedLeaseNotOffered?.();
    }
  }, [status, uploadOffered]); // eslint-disable-line react-hooks/exhaustive-deps

  if (status !== 'valid') {
    return (
      <PreviewStatusView
        testIdPrefix="mi"
        status={status}
        message={message}
        onRetry={retry}
      />
    );
  }
  return (
    <MoveInImpactList
      impacts={impacts}
      correctedLease={correctedLease}
      onUploadCorrectedLease={onUploadCorrectedLease}
      uploadButtonRef={uploadButtonRef}
    />
  );
};

export default MoveInCorrectionImpacts;
